import { Controller } from "@hotwired/stimulus"
import { activeServiceWorker, isStandalone, pushSupported, registerServiceWorker } from "../lib/pwa"

// Карточка «Уведомления» в личном кабинете: спрашивает разрешение и хранит
// подписку браузера на сервере (PushSubscriptionsController).
//
// Появляется она в двух случаях: человек зашёл из установленного приложения —
// то есть согласился на приложение и логично спросить про напоминания — или
// уведомления в этом браузере уже включены, и тогда карточка нужна, чтобы их
// можно было выключить там же, где включали. В обычной вкладке у того, кто
// ничего не включал, карточки нет: разрешение, выпрошенное на сайте, который
// человек ещё не поставил себе на экран, браузеры и сами душат.
//
// Разрешение и подписка — разные вещи, и состояние карточки складывается из
// обеих: разрешение живёт в браузере и переживает выход из аккаунта, а строка
// на сервере — то, куда мы на самом деле умеем что-то отправить.
export default class extends Controller {
  static targets = ["intro", "enabled", "denied", "status", "enableButton", "disableButton"]
  static values = { publicKey: String }

  connect() {
    if (!pushSupported() || !this.publicKeyValue) return this.hide()

    // refresh() ходит и в браузер, и в сеть, а connect() ничего не ждёт: без
    // catch любой отказ стал бы unhandled rejection и карточка молча исчезла бы.
    this.refresh().catch((error) => console.warn("Push card setup failed", error))
  }

  // Каждая ветка здесь заканчивается либо show(), либо hide(), и «оставить как
  // есть» среди них нет: скрытая разметка приходит с сервера только при первой
  // отрисовке, а дальше Turbo восстанавливает страницу из снимка — снятого,
  // возможно, при видимой карточке. Уведомления, выключенные с тех пор в другой
  // вкладке, вернулись бы карточкой «включены» и кнопкой, которой нечего снимать.
  async refresh() {
    const registration = await registerServiceWorker()
    if (!registration) return this.hide()

    this.subscription = await registration.pushManager.getSubscription()

    if (!isStandalone() && !this.subscription) return this.hide()

    if (Notification.permission === "denied") return this.show(this.deniedTarget)

    if (Notification.permission === "granted" && this.subscription) {
      // Сохраняем на каждой загрузке кабинета, без всякого «этот endpoint уже
      // отправляли». Кому принадлежит строка с этим endpoint'ом, знает только
      // сервер: браузерная подписка переживает выход из аккаунта, и на одном
      // телефоне люди меняются (см. комментарий PushSubscription.record). Любая
      // память на стороне браузера в этом ошибается — вошёл A, потом B, потом
      // снова A: у A всё ещё записано «сохранено», а строка уже принадлежит B,
      // и напоминания B приходят на экран A.
      //
      // Цена лишнего запроса — один идемпотентный upsert на открытие страницы
      // (контроллер к тому же rate-limited); цена пропущенного — уведомления,
      // которые молчат навсегда и никак себя не выдают.
      try {
        await this.save(this.subscription)
      } catch (error) {
        // Не сохранили — уведомления не придут, но карточку всё равно
        // показываем: выключить их человек должен мочь там же, где включал.
        console.warn("Push subscription refresh failed", error)
      }

      return this.show(this.enabledTarget)
    }

    this.show(this.introTarget)
  }

  // «Включить уведомления». Разрешение спрашивается только отсюда — то есть в
  // ответ на нажатие: браузеры отклоняют запрос без жеста человека, а Safari
  // такой сайт запоминает.
  //
  // Отсюда же и порядок: requestPermission() идёт первым действием, без единого
  // ожидания перед ним. Жест человека живёт считанные секунды, и всё, что мы
  // успеем подождать до запроса, отнимается у самого запроса.
  async enable() {
    this.enableButtonTarget.disabled = true
    this.setStatus("")

    try {
      // Значение берём и из промиса, и из Notification.permission: Safari
      // помнит callback-форму requestPermission() и в ней резолвит undefined —
      // то есть «не granted», и человек, только что нажавший «Разрешить»,
      // получал бы молчаливый выход из этой функции и ту же кнопку.
      const permission = (await Notification.requestPermission()) || Notification.permission

      if (permission === "denied") return this.show(this.deniedTarget)
      // Окно закрыли, не ответив. Молча выходить нельзя — на экране осталась бы
      // ровно та же кнопка, что и до нажатия, без единого слова о том, почему.
      if (permission !== "granted") return this.setStatus("Уведомления не включены: разрешение не выдано.", "note")

      // Именно активного: подписаться на неактивном воркере нельзя, а
      // регистрация к этому моменту могла и не состояться вовсе.
      const registration = await activeServiceWorker()
      if (!registration) return this.setStatus("Браузер не поддерживает уведомления на этой странице.")

      const subscription = await this.subscribe(registration)
      if (!subscription) return this.setStatus(TAP_AGAIN, "note")

      await this.save(subscription)
      this.subscription = subscription
      this.show(this.enabledTarget)
    } catch (error) {
      console.warn("Push subscription failed", error)
      this.setStatus("Не удалось включить уведомления. Попробуйте ещё раз.")
    } finally {
      this.enableButtonTarget.disabled = false
    }
  }

  // Подписка браузера у push-сервиса. Отказ здесь не бросается наружу, потому
  // что самый частый его повод — не поломка, а время.
  //
  // Safari (в том числе на iOS, где карточка только и живёт) требует, чтобы
  // subscribe() шёл из свежего жеста человека, а жест этот истекает за
  // несколько секунд. Первое в жизни нажатие показывает окно разрешения, и
  // человек читает его дольше — так что subscribe() сразу после «Разрешить»
  // отклоняется с NotAllowedError, хотя разрешение уже выдано. Второе нажатие
  // проходит: окна больше нет, requestPermission() отвечает мгновенно, жест ещё
  // жив. Ровно это и видно снаружи как «согласился, и ничего не произошло».
  //
  // Поэтому отказ превращается в просьбу нажать ещё раз, а не в «не удалось»:
  // состояние после него — разрешение есть, подписки нет — чинится именно этим
  // нажатием. Само же оно не делается за человека: подписать браузер, никого не
  // спросив, — это ещё и отменить чужое «Отключить», после которого разрешение
  // остаётся выданным.
  async subscribe(registration) {
    try {
      return await registration.pushManager.subscribe({
        // Обязательно true: браузер разрешает push только с обещанием, что
        // каждое сообщение станет видимым уведомлением. Мы его и держим — см.
        // обработчик `push` в service worker.
        userVisibleOnly: true,
        applicationServerKey: decodeKey(this.publicKeyValue)
      })
    } catch (error) {
      console.warn("Push subscribe rejected", error)

      // Подписка могла всё-таки появиться — например, её создало предыдущее
      // нажатие, чей ответ мы не дождались. Тогда просить о чём-либо не нужно:
      // её достаточно сохранить.
      return registration.pushManager.getSubscription()
    }
  }

  // «Отключить». Сначала сервер, потом браузер: отписавшись первыми, мы
  // потеряли бы endpoint и оставили на сервере строку, в которую он будет
  // писать в пустоту, пока push-сервис не ответит 410.
  async disable() {
    this.disableButtonTarget.disabled = true

    try {
      const subscription = this.subscription || (await this.currentSubscription())
      if (!subscription) return this.show(this.introTarget)

      await this.forget(subscription.endpoint)
      await subscription.unsubscribe()

      this.subscription = null
      this.show(this.introTarget)
    } catch (error) {
      console.warn("Push unsubscribe failed", error)
      this.setStatus("Не удалось отключить уведомления. Попробуйте ещё раз.")
    } finally {
      this.disableButtonTarget.disabled = false
    }
  }

  async currentSubscription() {
    const registration = await registerServiceWorker()

    return registration ? registration.pushManager.getSubscription() : null
  }

  // Успех — только 201 (контроллер отвечает `head :created`). `response.ok`
  // здесь мало: с потерянной сессией require_authentication отвечает редиректом
  // на страницу входа, fetch проходит по нему сам и приносит 200 с html —
  // «успех», после которого на сервере нет никакой подписки. `redirected` ловит
  // тот же обман на любом другом редиректе.
  async save(subscription) {
    const { endpoint, keys } = subscription.toJSON()

    const response = await this.post("POST", { subscription: { endpoint, keys } })

    if (response.redirected || response.status !== 201) {
      throw new Error(`Subscription rejected: ${response.status}`)
    }
  }

  // Тоже по коду, и по той же причине — плюс своя: порядок «сначала сервер,
  // потом браузер» (см. disable) держится только если отказ сервера виден. 429
  // от собственного лимита или 422 из-за протухшего CSRF-токена оставили бы
  // живую строку с endpoint'ом, который уже никто не слушает.
  async forget(endpoint) {
    const response = await this.post("DELETE", { endpoint })

    if (response.redirected || response.status !== 204) {
      throw new Error(`Unsubscribe rejected: ${response.status}`)
    }
  }

  post(method, body) {
    return fetch("/push_subscription", {
      method: method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
      },
      // Нужны cookie сессии: сервер берёт владельца подписки из неё, а не из
      // тела запроса.
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
  }

  show(target) {
    for (const section of [this.introTarget, this.enabledTarget, this.deniedTarget]) {
      section.hidden = section !== target
    }

    this.element.hidden = false
  }

  hide() {
    this.element.hidden = true
  }

  // Тон — часть сообщения: «нажмите ещё раз» и «разрешение не выдано» это
  // следующий шаг и решение человека, а красным здесь говорится только о том,
  // что сломалось.
  setStatus(message, tone = "error") {
    this.statusTarget.textContent = message
    this.statusTarget.hidden = message === ""
    this.statusTarget.classList.toggle("dash-flash-err", tone === "error")
  }
}

// Разрешение получено, подписки нет — состояние, из которого выходят ещё одним
// нажатием той же кнопки (см. subscribe). Второй раз окна разрешения уже не
// будет.
const TAP_AGAIN = "Разрешение получено. Нажмите «Включить уведомления» ещё раз, чтобы завершить."

// VAPID-ключ приезжает из Ruby как base64url, а `applicationServerKey` берёт
// только сырые байты.
function decodeKey(base64) {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")
  const binary = window.atob(padded.replace(/-/g, "+").replace(/_/g, "/"))

  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}
